import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    Container,
    Grid,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
} from "@mui/material";

import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DefaultNavbar from "../../components/TopNavBar/DefaultNavBar";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Upload as UploadIcon, Download as DownloadIcon, KeyboardArrowDown } from "@mui/icons-material";
import { useCimbeo } from "../../context";

const CustomDropdownIcon = () => (
    <KeyboardArrowDown sx={{
        width: 28,
        height: 28,
        color: '#2B5587',
        position: 'absolute',
        right: 12,
        top: 'calc(50% - 14px)',
        pointerEvents: 'none'
    }} />
);

const UserSelectIcon = () => (
    <AddIcon sx={{
        width: 24,
        height: 24,
        color: '#2B5587',
        position: 'absolute',
        right: 12,
        top: 'calc(50% - 12px)',
        pointerEvents: 'none'
    }} />
);

function ProjectManagement() {
    const navigate = useNavigate();
    const { projects: contextProjects, setProjects: setContextProjects, ingestedDocuments, allUsers, setAllUsers, setIngestedDocuments, user } = useCimbeo();
    const [projects, setProjects] = useState((contextProjects || []).map(project => ({
        ...project,
        documents: project.documents || []
    })));
    const [selectedProject, setSelectedProject] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [newProjectDialogOpen, setNewProjectDialogOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        name: "",
        description: "",
        access: [],
    });

    // Get users who have access to the selected project
    const getProjectUsers = () => {
        if (!selectedProject) return [];
        return allUsers.filter(user => 
            (user.projectIds || []).includes(selectedProject.id)
        );
    };

    // Update local state when context changes
    useEffect(() => {
        setProjects(contextProjects || []);
    }, [contextProjects]);

    const handleProjectSelect = (event) => {
        const project = projects.find(p => p.id === event.target.value);
        setSelectedProject(project);
        if (project) {
            // Get users who currently have access to this project
            const usersWithAccess = allUsers
                .filter(user => (user.projectIds || []).includes(project.id))
                .map(user => user.id);
            
            setEditForm({
                name: project.title,
                description: project.description,
                access: usersWithAccess,
            });
        }
    };

    const handleEditOpen = () => {
        setEditDialogOpen(true);
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
    };

    const handleNewProjectOpen = () => {
        setSelectedProject(null);
        setNewProjectDialogOpen(true);
        setEditForm({
            name: "",
            description: "",
            access: [user.id], // Include current user by default
        });
    };

    const handleNewProjectClose = () => {
        setNewProjectDialogOpen(false);
    };

    const handleFormChange = (field) => (event) => {
        setEditForm(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleSave = () => {
        let updatedProjects;
        let updatedUsers = [...allUsers];

        if (selectedProject) {
            // Update existing project
            updatedProjects = projects.map(p => 
                p.id === selectedProject.id 
                    ? { 
                        ...p, 
                        title: editForm.name,
                        description: editForm.description,
                        documents: p.documents || []
                    }
                    : p
            );

            // Update users' projectIds
            updatedUsers = allUsers.map(user => {
                const hasAccess = editForm.access.includes(user.id);
                const currentProjectIds = user.projectIds || [];
                
                if (hasAccess && !currentProjectIds.includes(selectedProject.id)) {
                    // Add project to user's access
                    return {
                        ...user,
                        projectIds: [...currentProjectIds, selectedProject.id]
                    };
                } else if (!hasAccess && currentProjectIds.includes(selectedProject.id)) {
                    // Remove project from user's access
                    return {
                        ...user,
                        projectIds: currentProjectIds.filter(id => id !== selectedProject.id)
                    };
                }
                return user;
            });
        } else {
            // Add new project
            const newProject = {
                id: projects.length + 1,
                title: editForm.name,
                description: editForm.description,
                documents: [], // Initialize empty documents array for new projects
                requirements: [] // Initialize empty requirements array for new projects
            };
            updatedProjects = [...projects, newProject];

            // Update users' projectIds for new project
            updatedUsers = allUsers.map(user => {
                if (editForm.access.includes(user.id)) {
                    const currentProjectIds = user.projectIds || [];
                    return {
                        ...user,
                        projectIds: [...currentProjectIds, newProject.id]
                    };
                }
                return user;
            });
        }
        
        // Update both local state and context
        setProjects(updatedProjects);
        setContextProjects(updatedProjects);
        setAllUsers(updatedUsers);
        handleEditClose();
        handleNewProjectClose();
    };

    const handleDelete = () => {
        if (selectedProject) {
            // Remove project from projects array
            const updatedProjects = projects.filter(p => p.id !== selectedProject.id);
            
            // Remove project ID from all users' projectIds arrays
            const updatedUsers = allUsers.map(user => ({
                ...user,
                projectIds: (user.projectIds || []).filter(id => id !== selectedProject.id)
            }));

            // Update both local state and context
            setProjects(updatedProjects);
            setContextProjects(updatedProjects);
            setAllUsers(updatedUsers);
            setSelectedProject(null);
        }
    };

    const handleDocumentUpload = () => {
        if (selectedProject) {
            navigate('/artifact-ingestion', { 
                state: { 
                    selectedProject: {
                        id: selectedProject.id,
                        title: selectedProject.title
                    }
                }
            });
        }
    };

    const getProjectDocuments = () => {
        if (!selectedProject) return [];
        return ingestedDocuments.filter(doc => doc.projectName === selectedProject.title);
    };

    const handleDocumentDelete = (documentId) => {
        if (selectedProject) {
            setIngestedDocuments(prev => prev.filter(doc => doc.id !== documentId));
        }
    };

    const handleDocumentDownload = (documentId) => {
        // Implement document download logic
        console.log("Downloading document:", documentId);
    };

    return (
        <DashboardLayout>
            <DefaultNavbar />
            <MDBox py={3}>
                <Container maxWidth="xl">
                    <MDBox mb={3}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <MDTypography variant="h5">Project Management</MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6} sx={{ textAlign: { xs: "left", md: "right" } }}>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleNewProjectOpen}
                                >
                                    New Project
                                </Button>
                            </Grid>
                        </Grid>
                    </MDBox>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ overflow: 'visible', mb: 3, backgroundColor: "navy.main" }}>
                                <MDBox p={3}>
                                    <MDTypography variant="h6" mb={2}>Projects</MDTypography>
                                    <FormControl fullWidth>
                                        <Select
                                            fullWidth
                                            value={selectedProject?.id || ""}
                                            onChange={handleProjectSelect}
                                            displayEmpty
                                            IconComponent={() => <CustomDropdownIcon />}
                                            sx={{
                                                height: "56px",
                                                '& .MuiSelect-select': {
                                                    padding: '16.5px',
                                                    fontSize: '1rem',
                                                },
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                Select a project
                                            </MenuItem>
                                            {projects.map((project) => (
                                                <MenuItem key={project.id} value={project.id}>
                                                    {project.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </MDBox>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            {selectedProject ? (
                                <Card sx={{ overflow: 'visible', backgroundColor: "navy.main" }}>
                                    <MDBox p={3}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                            <MDTypography variant="h6">{selectedProject.title}</MDTypography>
                                            <Stack direction="row" spacing={1}>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<EditIcon />}
                                                    onClick={handleEditOpen}
                                                    sx={{
                                                        borderColor: 'gold.main',
                                                        color: 'gold.main',
                                                        '&:hover': {
                                                            borderColor: 'gold.main',
                                                            bgcolor: 'rgba(43, 85, 135, 0.04)',
                                                        },
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<DeleteIcon />}
                                                    onClick={handleDelete}
                                                    sx={{
                                                        borderColor: '#dc3545',
                                                        color: '#dc3545',
                                                        '&:hover': {
                                                            borderColor: '#dc3545',
                                                            bgcolor: 'rgba(220, 53, 69, 0.04)',
                                                        },
                                                    }}
                                                >
                                                    Delete
                                                </Button>
                                            </Stack>
                                        </Box>
                                        <MDTypography variant="body2" mb={3}>
                                            {selectedProject.description}
                                        </MDTypography>
                                        <Box mb={3}>
                                            <MDTypography variant="subtitle2" mb={1}>Access:</MDTypography>
                                            <Stack direction="row" spacing={1}>
                                                {getProjectUsers().map((user) => (
                                                    <Chip
                                                        key={user.id}
                                                        label={user.realName || user.username}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'olive_green.main',
                                                            color: '#FFFFFF',
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Box>
                                        <Divider sx={{ my: 3 }} />
                                        <Box>
                                            <MDTypography variant="h6" mb={2}>Document Management</MDTypography>
                                            <Button
                                                variant="contained"
                                                startIcon={<UploadIcon />}
                                                onClick={handleDocumentUpload}
                                                sx={{
                                                    mb: 3,
                                                }}
                                            >
                                                Ingest Artifacts
                                            </Button>
                                            
                                            {(getProjectDocuments().length > 0) ? (
                                                <MDBox>
                                                    <MDTypography variant="subtitle2" mb={2}>Project Documents</MDTypography>
                                                    <Stack spacing={2}>
                                                        {getProjectDocuments().map((doc) => (
                                                            <MDBox
                                                                bgColor="slate_gray"
                                                                key={doc.id}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    p: 2,
                                                                    borderRadius: 1,
                                                                }}
                                                            >
                                                                <MDBox>
                                                                    <MDTypography variant="body2" color="dark_teal">{doc.name}</MDTypography>
                                                                    <MDTypography variant="caption" color="black">
                                                                      Uploaded by: {doc.uploadedBy} • Type: {doc.type} • Status: {doc.status} • {new Date(doc.timestamp).toLocaleDateString()}
                                                                    </MDTypography>
                                                                </MDBox>
                                                                <Stack direction="row" spacing={1}>
                                                                    <Tooltip title="Download Document" arrow placement="top">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleDocumentDownload(doc.id)}
                                                                            sx={{
                                                                                color: '#2B5587',
                                                                                '&:hover': {
                                                                                    bgcolor: 'rgba(43, 85, 135, 0.04)',
                                                                                },
                                                                            }}
                                                                        >
                                                                            <DownloadIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Delete Document" arrow placement="top">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleDocumentDelete(doc.id)}
                                                                            sx={{
                                                                                color: '#dc3545',
                                                                                '&:hover': {
                                                                                    bgcolor: 'rgba(220, 53, 69, 0.04)',
                                                                                },
                                                                            }}
                                                                        >
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </MDBox>
                                                        ))}
                                                    </Stack>
                                                </MDBox>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No documents uploaded yet.
                                                </Typography>
                                            )}
                                        </Box>
                                    </MDBox>
                                </Card>
                            ) : (
                                <Card>
                                    <MDBox p={3} textAlign="center">
                                        <MDTypography variant="body1" color="text.secondary">
                                            Please select a project to view details and manage documents.
                                        </MDTypography>
                                    </MDBox>
                                </Card>
                            )}
                        </Grid>
                    </Grid>
                </Container>
            </MDBox>

            {/* Edit Project Dialog */}
            <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Project</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Project Name"
                            value={editForm.name}
                            onChange={handleFormChange("name")}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: "56px",
                                },
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={editForm.description}
                            onChange={handleFormChange("description")}
                            multiline
                            rows={4}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="project-users-label">Project Users</InputLabel>
                            <Select
                                labelId="project-users-label"
                                label="Project Users"
                                fullWidth
                                multiple
                                value={editForm.access}
                                onChange={handleFormChange("access")}
                                IconComponent={() => <UserSelectIcon />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((userId) => {
                                            const user = allUsers.find(u => u.id === userId);
                                            return user ? (
                                                <Chip
                                                    key={userId}
                                                    label={user.realName || user.username}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#2B5587',
                                                        color: '#FFFFFF',
                                                    }}
                                                />
                                            ) : null;
                                        })}
                                    </Box>
                                )}
                                sx={{
                                    height: "56px",
                                    '& .MuiSelect-select': {
                                        padding: '16.5px',
                                        fontSize: '1rem',
                                    },
                                }}
                            >
                                {allUsers.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.realName || user.username}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditClose}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ bgcolor: "#2B5587", color: "#FFFFFF" }}>
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* New Project Dialog */}
            <Dialog open={newProjectDialogOpen} onClose={handleNewProjectClose} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Project</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Project Name"
                            value={editForm.name}
                            onChange={handleFormChange("name")}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: "56px",
                                },
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={editForm.description}
                            onChange={handleFormChange("description")}
                            multiline
                            rows={4}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="new-project-users-label">Project Users</InputLabel>
                            <Select
                                labelId="new-project-users-label"
                                label="Project Users"
                                fullWidth
                                multiple
                                value={editForm.access}
                                onChange={handleFormChange("access")}
                                IconComponent={() => <UserSelectIcon />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const user = allUsers.find(u => u.id === value);
                                            return user ? (
                                                <Chip
                                                    key={value}
                                                    label={user.realName || user.username}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#2B5587',
                                                        color: '#FFFFFF',
                                                    }}
                                                />
                                            ) : null;
                                        })}
                                    </Box>
                                )}
                                sx={{
                                    height: "56px",
                                    '& .MuiSelect-select': {
                                        padding: '16.5px',
                                        fontSize: '1rem',
                                    },
                                }}
                            >
                                {allUsers.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.realName || user.username}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleNewProjectClose}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ bgcolor: "#2B5587", color: "#FFFFFF" }}>
                        Create Project
                    </Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}

export default ProjectManagement; 